import React, { useState, useEffect } from "react";
import Select from "react-select";
import Button from "../../../../../core/Button";
import Card from "../../../../../core/Card";
import Input from "../../../../../core/Inputs/DefaultInput";
import style from "../../../../style.module.css";
import Svg from "../../../../../component/common/Svg";
import Switch from "../../../../../core/Switch";
import DataTable from "react-data-table-component";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getSurveyGroupDetail } from "../../../../../store/actions/surveyGroupAction";
import { getUtmVariable } from "../../../../../store/actions/variableAction";
import { getSurveyUrl, addSurveyUrl, updateSurveyUrl, deleteSurveyUrl, updateLinkStatus } from "../../../../../store/actions/urlAction";
import { getTokenData } from "../../../../../store/actions/authAction";

const SURVEAY_URL = process.env.REACT_APP_USER_LINK || "https://survey.io";

export default function GroupShare() {
  const { t } = useTranslation();
  const { groupId } = useParams();
  const dispatch = useDispatch();
  const { groupDetail, groupDetailLoading } = useSelector((state) => state.surveyGroupData);
  const { utmVariableData } = useSelector((state) => state.variables);
  const urlData = useSelector((state) => state.urlData?.surveyUrlData || []);
  const urlDataLoading = useSelector((state) => state.urlData?.getSurveyUrlDataLoading);

  // UI State
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [currentlyDeletingLink, setCurrentlyDeletingLink] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentLinkID, setCurrentLinkID] = useState(null);
  const [btnText, setBtnText] = useState(t("survey.share.copy") || "Copy Link");
  const [linkName, setLinkName] = useState("");
  const [source, setSource] = useState("");
  const [medium, setMedium] = useState("");
  const [content, setContent] = useState("");
  const [campaign, setCampaign] = useState("");
  const [params, setParams] = useState([{ name: "", value: "" }]);
  const [userId, setUserId] = useState('');
  const [isNameError, setIsNameError] = useState(null);
  const [sourceError, setSourceError] = useState(null);
  const [mediumError, setMediumError] = useState(null);
  const [contentError, setContentError] = useState(null);
  const [campaignError, setCampaignError] = useState(null);

  // Fetch group details, UTM variables, and URLs on mount
  useEffect(() => {
    if (groupId) {
      dispatch(getSurveyGroupDetail(groupId));
      dispatch(getUtmVariable(null, groupId, 'group')); // Fetch group UTM variables
      dispatch(getSurveyUrl(null, groupId)); // Fetch group URLs
    }
  }, [groupId, dispatch]);

  useEffect(() => {
    dispatch(getTokenData(setUserId));
  }, [dispatch]);

  // Get primary survey ID from group detail
  const primarySurveyId = groupDetail?.su_default_survey_id || "";
  const groupName = groupDetail?.su_group_title || "Loading...";

  // Generate base group URL with format: /G/groupId/primarySurveyId
  const baseGroupUrl = primarySurveyId 
    ? `${SURVEAY_URL}/G/${groupId}/${primarySurveyId}`
    : `${SURVEAY_URL}/G/${groupId}`;

  // Helper functions
  const fetchUrl = () => {
    dispatch(getSurveyUrl(null, groupId));
  };

  const resetModal = () => {
    setEditMode(false);
    setCurrentLinkID(null);
    setIsVisibleModal(false);
    setLinkName("");
    setSource("");
    setMedium("");
    setContent("");
    setCampaign("");
    setParams([{ name: "", value: "" }]);
    setIsNameError(null);
    setSourceError(null);
    setMediumError(null);
    setContentError(null);
    setCampaignError(null);
  };

  const getFinalLink = (linkData) => {
    const link = linkData.url;
    const lid = linkData.survey_link_id;
    return `${link}&lid=${lid}`;
  };

  const getUrl = () => {
    const utmParams = [];
    if (source) utmParams.push(`utm_source=${source}`);
    if (medium) utmParams.push(`utm_medium=${medium}`);
    if (campaign) utmParams.push(`utm_campaign=${campaign}`);
    if (content) utmParams.push(`utm_content=${content}`);
    
    // Add custom parameters
    const customParams = params
      .filter(param => param.name && param.value)
      .map(param => `${param.name}=${param.value}`)
      .join('&');
    
    const allParams = [...utmParams, customParams].filter(Boolean).join('&');
    return allParams ? `${baseGroupUrl}?${allParams}` : baseGroupUrl;
  };

  const handleCopy = (link) => {
    navigator?.clipboard?.writeText(link);
    setBtnText("Link copied");
    setTimeout(() => {
      setBtnText(t("survey.share.copy") || "Copy Link");
    }, 700);
  };

  const handleEdit = (data) => {
    try {
      const url = new URL(data.url ?? "");
      const searchParams = new URLSearchParams(url.search);
      
      setLinkName(data?.link_name || "");
      setSource(searchParams.get("utm_source") || "");
      setMedium(searchParams.get("utm_medium") || "");
      setContent(searchParams.get("utm_content") || "");
      setCampaign(searchParams.get("utm_campaign") || "");
      
      // Parse variables and ensure at least one empty row
      const parsedParams = JSON.parse(data?.variables || "[]");
      setParams(parsedParams.length > 0 ? parsedParams : [{ name: "", value: "" }]);
    } catch (error) {
      console.error("URL parsing error", error);
    }

    setCurrentLinkID(data.survey_link_id);
    setEditMode(true);
    setIsVisibleModal(true);
  };

  const handleDelete = ({ survey_link_id }) => {
    dispatch(deleteSurveyUrl(survey_link_id));
    setCurrentlyDeletingLink(null);
    setIsDeleteModalVisible(false);
    setTimeout(() => fetchUrl(), 300);
  };

  const handleRequiredChange = (e) => {
    const payload = {
      status: e.target.checked ? 1 : 0,
      survey_link_id: e.target.value,
    };
    dispatch(updateLinkStatus(payload));
  };

  const handleSave = () => {
    // Reset all errors
    setIsNameError(null);
    setSourceError(null);
    setMediumError(null);
    setContentError(null);
    setCampaignError(null);

    // Validate all required fields
    let hasError = false;

    if (!linkName.trim()) {
      setIsNameError('Link name required');
      hasError = true;
    }

    if (!source.trim()) {
      setSourceError('Source required');
      hasError = true;
    }

    if (!medium.trim()) {
      setMediumError('Medium required');
      hasError = true;
    }

    if (!content.trim()) {
      setContentError('Content required');
      hasError = true;
    }

    if (!campaign.trim()) {
      setCampaignError('Campaign required');
      hasError = true;
    }

    // If any validation failed, stop here
    if (hasError) {
      return;
    }

    const payload = {
      survey_id: null,
      su_group_id: groupId,
      su_group_slug: groupId,
      link_scope: 'group',
      name: linkName,
      variables: JSON.stringify(params.filter(p => p.name && p.value)),
      url: getUrl(),
      added_by: userId,
    };

    const failCallback = (err) => {
      setIsNameError(err.name);
    };

    const successCallback = () => {
      fetchUrl();
    resetModal();
    };

    if (editMode) {
      dispatch(updateSurveyUrl({
        survey_link_id: currentLinkID,
        name: linkName,
        variables: JSON.stringify(params.filter(p => p.name && p.value)),
        url: getUrl(),
      }, successCallback));
    } else {
      dispatch(addSurveyUrl(payload, successCallback, failCallback));
    }
  };

  const setParamsValue = (index, key, value) => {
    const newParams = [...params];
    newParams[index][key] = value;
    setParams(newParams);
  };

  const addParam = () => {
    if (params[params.length - 1].name && params[params.length - 1].value) {
      setParams([...params, { name: "", value: "" }]);
    }
  };

  const removeParam = (index) => {
    if (params.length > 1) {
      const newParams = params.filter((_, i) => i !== index);
      setParams(newParams);
    }
  };
  
  // Extract UTM variables from Redux state by type
  const sourceVariables = utmVariableData.filter(utm => utm.su_suv_type === 1).map(utm => utm.su_var_name);
  const mediumVariables = utmVariableData.filter(utm => utm.su_suv_type === 2).map(utm => utm.su_var_name);
  const campaignVariables = utmVariableData.filter(utm => utm.su_suv_type === 3).map(utm => utm.su_var_name);

  // Always show only the base URL in the input field (without UTM params or lid)
  const currentUrl = baseGroupUrl;

  // Dynamic options for Select components from group UTM variables
  // Include current selected values even if they don't exist in UTM variables (for backward compatibility)
  const getSourceOptions = () => {
    const options = sourceVariables.map((item) => ({ value: item, label: item }));
    if (source && !sourceVariables.includes(source)) {
      options.push({ value: source, label: source });
    }
    return options;
  };

  const getMediumOptions = () => {
    const options = mediumVariables.map((item) => ({ value: item, label: item }));
    if (medium && !mediumVariables.includes(medium)) {
      options.push({ value: medium, label: medium });
    }
    return options;
  };

  const getCampaignOptions = () => {
    const options = campaignVariables.map((item) => ({ value: item, label: item }));
    if (campaign && !campaignVariables.includes(campaign)) {
      options.push({ value: campaign, label: campaign });
    }
    return options;
  };

  const sourceOptions = getSourceOptions();
  const mediumOptions = getMediumOptions();
  const campaignOptions = getCampaignOptions();
  

  // Columns for DataTable
  const columns = [
    {
      name: t("survey.share.name") || "Name",
      cell: (row) => {
        return (
          <a
            href={getFinalLink(row)}
            className="linkedIn"
            target="_blank"
            rel="noreferrer"
          >
            <Svg name="link" />
            <span className="ms-2" title={row.link_name ?? ""}>
              {row.link_name ?? ""}
            </span>
          </a>
        );
      },
    },
    {
      name: t("survey.share.res") || "Responses",
      selector: (row) => row.count ?? 0,
    },
    {
      name: t("survey.share.createdDate") || "Created Date",
      selector: (row) => {
        const date = new Date(row.add_date_time);
        return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
      },
    },
    {
      name: t("survey.share.status") || "Status",
      cell: (row) => {
        return (
          <div className="action-wrapper d-flex accordianSwitch">
            <Switch
              type="dense"
              color="default"
              checked={Number(row.is_active) === 1}
              label={""}
              disabled={false}
              fill={false}
              rtl={false}
              leftPosition={false}
              className="SwitchCustom"
              value={row.survey_link_id}
              onChange={handleRequiredChange}
            />
          </div>
        );
      },
    },
    {
      name: t("survey.customVariable.action") || "Action",
      cell: (row) => {
        return (
          <div className="d-flex">
            <button
              className="action-btn"
              onClick={() => handleEdit(row)}
            >
              <Svg name="table-edit" />
            </button>
            <button
              className="action-btn"
              onClick={() => handleCopy(getFinalLink(row))}
            >
              <Svg name="table-copy" />
            </button>
            <button
              className="action-btn"
              onClick={() => {
                setCurrentlyDeletingLink(row);
                setIsDeleteModalVisible(true);
              }}
            >
              <Svg name="table-delete" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex-grow-1 dashboard-page-outer">
        
        <div className="dashboard-page-wrap">
          
          <div className="dashboard-page-content">
            <div className="page-heading d-md-flex align-items-center justify-content-between mb-20">
              <div className="head-lef">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/admin/survey/groups">Groups</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={`/admin/survey/groups/${groupId}`}>{groupName}</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      <strong>{t("survey.share.tittle") || "Publish & Share"}</strong>
                    </li>
                  </ol>
                </nav>
                <h1>{t("survey.share.tittle") || "Publish & Share"}</h1>
              </div>
            </div>
            <h4>{t("survey.share.text1") || "How would you like to share survey group to people?"}</h4>
            <div className="copy-link-wrapper d-flex mx-0">
              <div className="flex-grow-1">
                <Input
                  type="text"
                  view="outlined"
                  color="default"
                  label=""
                 placeholder={groupDetailLoading ? "Loading..." : `${SURVEAY_URL}/G/${groupId}/${primarySurveyId || 'xxx'}`}
                  fill
                  className={`formsinput ${style.card__exp}`}
                  value={currentUrl}
                  readOnly
                  disabled={groupDetailLoading || !primarySurveyId}
                />
              </div>
              <div className="button-wrapper ps-3">
                <Button
                  className={"formsbutton"}
                  type="default"
                  view="filled"
                  color="primary"
                  text={groupDetailLoading ? "Loading..." : btnText}
                  fill={true}
                  disabled={groupDetailLoading || !primarySurveyId}
                  onClick={() => {
                    handleCopy(currentUrl);
                  }}
                />
              </div>
            </div>
            {!groupDetailLoading && !primarySurveyId && (
              <div className="alert alert-warning mt-3" role="alert">
                <strong>No Primary Survey:</strong> Please set a primary survey in the group to generate shareable links.
              </div>
            )}
            <div className="table-wrap">
              <h4>{t("survey.share.campaign") || "Campaign"}</h4>
              <div className="table-responsive">
                <div className="mb-4 custom-data-table">
                  <DataTable
                    columns={columns}
                    data={urlData}
                    pagination
                    progressPending={urlDataLoading}
                  />
                </div>
              </div>
            </div>
            <div className="button-wrap create-url-btn text-center">
              <button
                className="btn btn-bordered"
                aria-expanded="false"
                disabled={!primarySurveyId}
                onClick={() => {
                  resetModal();
                  setIsVisibleModal(true);
                }}
              >
                {t("survey.share.create") || "Create new URL"}
              </button>
            </div>
          </div>

          {/* Build Campaign URL Modal */}
          <div
            className={`customvar-modal modal custom-modal fade url-build-modal ${
              isVisibleModal ? "show" : ""
            }`}
            style={{ display: `${isVisibleModal ? "block" : "none"}` }}
            id="urlBuildModal"
            tabIndex={-1}
            aria-labelledby="urlBuildModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-body">
                  <div className={"formcontainer"}>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSave();
                      }}
                    >
                      <div className={"url-build-modal-wrapper"}>
                        <Card
                          view="raised"
                          className={`formstyle p-0 m-0 ${style.card}`}
                        >
                          <div className="heading">
                            <h4>{editMode ? t("survey.share.edit1") || "Edit campaign URL" : "Build campaign URL"}</h4>
                          </div>
                          <div className="form-wrapper">
                            <div className="description">
                              <p>{t("survey.share.text2") || "Add UTM parameters to track your campaign performance"}</p>
                            </div>
                            <div className="form-fields-wrapper">
                              <div className="row">
                                <div className="col-md-2 text-md-end mb-4">
                                  <label className="col-form-label">
                                    {t("survey.share.name") || "Name"}
                                  </label>
                                </div>
                                <div className="col-md-10">
                                  <div className="position-relative">
                                    <Input
                                      type="text"
                                      view="outlined"
                                      color="default"
                                      label=""
                                      //placeholder={t("survey.share.namePlaceholder") || "Enter campaign name"}
                                      clearButton
                                      fill
                                      className={"formsinput"}
                                      value={linkName}
                                      onChange={(e) => {
                                        setLinkName(e.target.value);
                                        setIsNameError(null);
                                      }}
                                      onClear={() => setLinkName("")}
                                    />
                                    {isNameError && (
                                      <div className="validation-msg text-danger mt-1">
                                        {isNameError}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-md-2 text-md-end">
                                  <label className="col-form-label">
                                    {t("survey.share.source") || "Source"}
                                  </label>
                                </div>
                                <div className="col-md-10 ">
                                  <div className="position-relative">
                                    <Select
                                      isClearable
                                      options={sourceOptions}
                                      placeholder={t("survey.select")}
                                      value={
                                        sourceOptions.find((opt) => opt.value === source) || null
                                      }
                                      onChange={(selectedOpt) => {
                                        setSource(selectedOpt?.value ?? "");
                                        setSourceError(null);
                                      }}
                                    />
                                    {sourceError && (
                                      <div className="validation-msg text-danger mt-1">
                                        {sourceError}
                                      </div>
                                    )}
                                  </div>
                                  <div className="help-text mb-3">
                                    {t("survey.share.text2") || "Add UTM parameters to track your campaign performance"}
                                  </div>
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-md-2 text-md-end">
                                  <label className="col-form-label">
                                    {t("survey.share.medium") || "Medium"}
                                  </label>
                                </div>
                                <div className="col-md-10">
                                  <div className="position-relative">
                                    <Select
                                      isClearable
                                      placeholder={t("survey.select")}
                                      options={mediumOptions}
                                      value={
                                        mediumOptions.find((opt) => opt.value === medium) || null
                                      }
                                      onChange={(selectedOpt) => {
                                        setMedium(selectedOpt?.value ?? "");
                                        setMediumError(null);
                                      }}
                                    />
                                    {mediumError && (
                                      <div className="validation-msg text-danger mt-1">
                                        {mediumError}
                                      </div>
                                    )}
                                  </div>
                                  <div className="help-text mb-3">
                                    {t("survey.share.text2") || "Add UTM parameters to track your campaign performance"}
                                  </div>
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-md-2 text-md-end">
                                  <label className="col-form-label">
                                    {t("survey.share.content") || "Content"}
                                  </label>
                                </div>
                                <div className="col-md-10">
                                  <div className="position-relative">
                                    <Input
                                      type="text"
                                      view="outlined"
                                      color="default"
                                      label=""
                                     // placeholder="Start Survey"
                                      clearButton
                                      fill
                                      className={"formsinput"}
                                      value={content}
                                      onChange={(e) => {
                                        setContent(e.target.value);
                                        setContentError(null);
                                      }}
                                      onClear={() => setContent("")}
                                    />
                                    {contentError && (
                                      <div className="validation-msg text-danger mt-1">
                                        {contentError}
                                      </div>
                                    )}
                                  </div>
                                  <div className="help-text mb-3">
                                    Identifies what specifically was clicked to bring the user to the site, such as a banner ad or a text link. It is often used for A/B testing and content-targeted ads. Example: utm_content=logolink or utm_content=textlink
                                  </div>
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-md-2 text-md-end">
                                  <label className="col-form-label">
                                    {t("survey.share.campaign") || "Campaign"}
                                  </label>
                                </div>
                                <div className="col-md-10">
                                  <div className="position-relative">
                                    <Select
                                      isClearable
                                      options={campaignOptions}
                                      placeholder={t("survey.select")}
                                      value={
                                        campaignOptions.find((opt) => opt.value === campaign) || null
                                      }
                                      onChange={(selectedOpt) => {
                                        setCampaign(selectedOpt?.value ?? "");
                                        setCampaignError(null);
                                      }}
                                    />
                                    {campaignError && (
                                      <div className="validation-msg text-danger mt-1">
                                        {campaignError}
                                      </div>
                                    )}
                                  </div>
                                  <div className="help-text mb-3">
                                    {t("survey.share.text2") || "Add UTM parameters to track your campaign performance"}
                                  </div>
                                  <div className="parameter-add-fields">
                                    <label htmlFor="">
                                      {t("survey.share.Parameters") || "Parameters"}
                                    </label>
                                    {params.map((param, index) => {
                                      const isLast = index === params.length - 1;
                                      return (
                                        <div className="d-flex" key={index}>
                                          <div className="flex-grow-1">
                                            <div className="row g-3">
                                              <div className="col-lg-6">
                                                <div className="mb-3 position-relative">
                                                  <Input
                                                    type="text"
                                                    view="outlined"
                                                    color="default"
                                                    label=""
                                                    //placeholder={t("survey.customVariable.name") || "Name"}
                                                    fill
                                                    className={"formsinput"}
                                                    value={param.name}
                                                    onChange={(e) =>
                                                      setParamsValue(index, "name", e.target.value)
                                                    }
                                                    onClear={() =>
                                                      setParamsValue(index, "name", "")
                                                    }
                                                  />
                                                </div>
                                              </div>
                                              <div className="col-lg-6">
                                                <div className="mb-3 position-relative">
                                                  <Input
                                                    type="text"
                                                    view="outlined"
                                                    color="default"
                                                    label=""
                                                    //placeholder={t("survey.customVariable.value") || "Value"}
                                                    fill
                                                    className={"formsinput"}
                                                    value={param.value}
                                                    onChange={(e) =>
                                                      setParamsValue(index, "value", e.target.value)
                                                    }
                                                    onClear={() =>
                                                      setParamsValue(index, "value", "")
                                                    }
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="ps-3">
                                            {!isLast && (
                                              <button
                                                className="btn btn-link text-danger"
                                                style={{ marginRight: "5px" }}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  removeParam(index);
                                                }}
                                                type="button"
                                              >
                                                {t("webhook.remove") || "Remove"}
                                              </button>
                                            )}
                                            {isLast && (
                                              <button
                                                className="btn btn-link"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  addParam();
                                                }}
                                                type="button"
                                              >
                                                {t("survey.share.add") || "Add"}
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="generated-url">
                            <div className="mb-3">
                              <label>{t("survey.share.url") || "URL"}</label>
                              <textarea
                                className="form-control"
                                rows="2"
                                name="link"
                                value={getUrl()}
                                readOnly
                              ></textarea>
                            </div>

                            <div className="button-wrap d-flex justify-content-end">
                              <button
                                type="button"
                                className="btn btn-bordered"
                                onClick={(e) => {
                                  e.preventDefault();
                                  resetModal();
                                }}
                              >
                                {t("survey.customVariable.cancel") || "Cancel"}
                              </button>
                              <button
                              type="button"
                                className="btn btn-primary ms-3"
                              onClick={handleSave}
                              >
                              {editMode ? t("survey.share.update") : t("survey.builder.save")}
                              </button>
                            </div>
                          </div>
                        </Card>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Delete Confirmation Modal */}
          <div
            className={`customvar-modal modal custom-modal fade deleteModal ${
              isDeleteModalVisible ? "show" : ""
            }`}
            style={{ display: `${isDeleteModalVisible ? "block" : "none"}` }}
            id="deleteLinkModal"
            tabIndex={-1}
            aria-labelledby="deleteModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body">
                  <div className={`formcontainer ${style.container}`}>
                    <div
                      className={
                        "formvawrapper delete-modal-wrap modal-sm" +
                        " " +
                        style.wrapper
                      }
                    >
                      <Card
                        view="raised"
                        className={`formstyle p-0 ${style.card}`}
                      >
                        <div className="delete-body-wrap delete-language">
                          <div className="heading">
                            <h3>{t("survey.customFormula.confirm_delete") || "Confirm Delete"}</h3>
                            <p>
                              {t("survey.share.del1") || "Are you sure you want to delete"}
                              <strong>
                                {" "}
                                {currentlyDeletingLink?.link_name ?? ""}
                              </strong>{" "}
                              {t("survey.customVariable.permanently") || "permanently?"}
                            </p>
                          </div>

                          <div className="button-wrap d-flex justify-content-end">
                            <button
                              type="button"
                              className="btn btn-bordered"
                              onClick={() => {
                                setIsDeleteModalVisible(false);
                                setCurrentlyDeletingLink(null);
                              }}
                            >
                              {t("settings.roles.delete_action.cancel") || "Cancel"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-delete ms-3"
                              onClick={() => {
                                if (currentlyDeletingLink) {
                                  handleDelete(currentlyDeletingLink);
                                }
                              }}
                            >
                              {t("survey.customVariable.confirm_delete") || "Delete"}
                            </button>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`modal-backdrop fade ${isVisibleModal || isDeleteModalVisible ? "show" : ""}`}
            style={{ display: `${isVisibleModal || isDeleteModalVisible ? "block" : "none"}` }}
          ></div>
        </div>
      </div>
    </>
  );
}
